import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { putPolicy } from '../../../api/schoolConfigApi';
import { showSuccess, showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import CollapsibleSection from '../../common/CollapsibleSection';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { usePassPolicy } from '../../../hooks/usePassPolicy';
import type { PassPolicy } from '../../../hooks/usePassPolicy';
import adminStyles from '../AdminPanel.module.css';
import styles from './PassPolicyPanel.module.css';

// New panel (NOT a like-for-like port) built as part of the Firebase ->
// hallpass-backend migration (Admin step 6). Replaces the
// `manageSystemSettings` callable's "Pass Limits" section of
// AdminPanel.js's System Settings (loadSystemSettings/savePassLimits/
// resetSettings handlers, ~lines 1291-1378; JSX ~lines 4960-5147) with a
// form against GET/PUT /schools/:schoolId/policy
// ({maxActivePasses, interval, maxPerInterval} —
// schoolConfigApi.js's getPolicy/putPolicy).
//
// NONE of the legacy fields map 1:1 onto this new shape, so this is a
// fresh form against the new PassPolicy model, not a converter — the
// legacy fields (perClassLimit, totalLimit, enabled, dailyPassLimit,
// defaultPassLimit, developerMode, confirmationTimeoutMinutes) are not
// read, auto-populated, or written here.
//
// PER-FIELD DISPOSITION of the 7 legacy fields (Migration Plan Open
// Question #18 — reconstructed from pre-migration AdminPanel.js and its
// Cloud Functions, commit b4d399fe, the parent of the migration commit
// b65b165f that deleted this section):
//   - perClassLimit -> mapped onto ClassSection.maxOutAtOnce. (Already
//     resolved before this pass.)
//   - totalLimit -> mapped onto maxActivePasses above. Legacy enforced this
//     as a school-wide concurrently-active-passes cap
//     (functions/src/queue/v2/checkPassLimitsV2.js's `schoolWideLimit`,
//     sourced from `totalLimit`, gating `schoolHasCapacity =
//     currentSchoolWideCount < schoolWideLimit`) — identical semantics to
//     maxActivePasses's "max passes active at once, school-wide".
//   - enabled -> confirmed dropped, no replacement needed. It only gated
//     the totalLimit/perClassLimit capacity checks (same file: `if
//     (limits.enabled) { checkQueueCapacity(...) }`) — NOT a school-wide
//     pass kill-switch (dailyPassLimit/defaultPassLimit's history checks
//     ran regardless of `enabled`, and pass creation was never blocked by
//     it alone). The "off" state it produced is reached the same way now
//     by leaving maxActivePasses / ClassSection.maxOutAtOnce blank (both
//     nullable == unlimited per this form's own placeholder text), so no
//     separate toggle is needed.
//   - dailyPassLimit -> mapped onto interval='DAY' + maxPerInterval below.
//     Legacy enforced this as a per-student count of passes since midnight
//     (checkPassLimitsV2.js's checkHistoryLimits `daily` check); the new
//     maxPerInterval count, windowed from getIntervalStart(policy.interval,
//     timezone) (hallpass-backend apps/passes-api/src/routes/passes.ts:
//     ~181-199), reproduces this when interval is set to DAY.
//   - defaultPassLimit -> mapped onto maxPerInterval (school-wide default)
//     + StudentPassLimit.maxPerInterval (per-student override) — the same
//     default-plus-override shape legacy used (enforced as
//     `semesterStudentLimit`, overridable per student via a Firestore doc,
//     per checkPassLimitsV2.js:88-148). Two narrowings versus legacy, both
//     already documented elsewhere rather than new findings: (1) legacy
//     tracked usage per class period independently; the new model counts
//     ALL of a student's passes in the interval window regardless of
//     period — flagged as a "DATA MODEL CHANGE" in
//     StudentPassLimitsPanel.js:26-35; (2) legacy's window was open-ended
//     ("since start of semester") and ran *simultaneously* with
//     dailyPassLimit's separate daily window, whereas PassPolicy has only
//     one `interval` value per school — a DAY cap and a longer default cap
//     can no longer both be active at once.
//   - developerMode -> confirmed dropped, no replacement needed. This was
//     the persisted flag behind the legacy "Developer Mode: Manual Period
//     Override" checkbox (pre-migration AdminPanel.js's quickSetOverride /
//     setStagingPeriod / setStagingABDay wiring) — the same feature
//     already resolved under Migration Plan Open Question #20: dropped as
//     a deliberate product decision, documented at
//     DevOverrideContext.js:28-31.
//   - confirmationTimeoutMinutes -> confirmed dropped, noted in PR #2's
//     body. (Already resolved before this pass.)
//
// CROSS-PANEL DEPENDENCY: StudentPassLimitsPanel.js's per-student
// maxPerInterval override requires `interval` to already be set here (the
// backend 422s "School must have a PassPolicy interval configured..."
// otherwise) — that panel fetches this same policy on mount to gate its
// numeric limit input.

/** The non-null PassPolicy interval values (`interval` minus its null). */
type PassInterval = NonNullable<PassPolicy['interval']>;

interface PassPolicyPanelProps {
  schoolId?: number | string;
  embedded?: boolean;
}

const INTERVAL_OPTIONS: { value: PassInterval | ''; label: string }[] = [
  { value: '', label: 'Not set' },
  { value: 'DAY', label: 'Day' },
  { value: 'WEEK', label: 'Week' },
  { value: 'MONTH', label: 'Month' },
];

/**
 * Narrows the free-form select value back to a PassPolicy interval (or null
 * for the "Not set" empty option) so it satisfies UpsertPassPolicyBody
 * without a cast. The select only ever holds an INTERVAL_OPTIONS value, so
 * every reachable input maps 1:1 onto the same value it already held.
 */
const toInterval = (value: string): PassInterval | null =>
  value === 'DAY' || value === 'WEEK' || value === 'MONTH' ? value : null;

const PassPolicyPanel = ({
  schoolId: schoolIdProp,
  embedded = false,
}: PassPolicyPanelProps = {}): React.ReactElement => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;

  const [loadingStates, setLoading] = useLoadingStates({
    savePolicy: false,
  });

  const {
    policy,
    loading: loadingPolicy,
    error: policyError,
    refetch: fetchPolicy,
  } = usePassPolicy(schoolId);
  const policyExists = policy != null;

  const [maxActivePasses, setMaxActivePasses] = useState('');
  const [policyInterval, setPolicyInterval] = useState('');
  const [maxPerInterval, setMaxPerInterval] = useState('');

  const applyPolicy = (policy: PassPolicy | null | undefined): void => {
    setMaxActivePasses(
      policy?.maxActivePasses != null ? String(policy.maxActivePasses) : ''
    );
    setPolicyInterval(policy?.interval || '');
    setMaxPerInterval(
      policy?.maxPerInterval != null ? String(policy.maxPerInterval) : ''
    );
  };

  useEffect(() => {
    applyPolicy(policy);
  }, [policy]);

  useEffect(() => {
    if (policyError) {
      logError(policyError, 'loadPolicy');
      showError(
        getContextualError(
          policyError,
          undefined,
          'Failed to load pass policy. Please try again.'
        )
      );
    }
  }, [policyError]);

  const savePolicy = async (): Promise<void> => {
    if ((policyInterval === '') !== (maxPerInterval === '')) {
      showError(
        'Interval and Max Passes per Interval must be set together, or both left blank.'
      );
      return;
    }

    if (schoolId == null) return;

    setLoading('savePolicy', true);
    try {
      const updatedPolicy = await putPolicy(schoolId, {
        maxActivePasses:
          maxActivePasses === '' ? null : Number(maxActivePasses),
        interval: policyInterval === '' ? null : toInterval(policyInterval),
        maxPerInterval: maxPerInterval === '' ? null : Number(maxPerInterval),
      });
      applyPolicy(updatedPolicy);
      await fetchPolicy();
      showSuccess('Pass policy saved.');
    } catch (error) {
      logError(error, 'savePolicy');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to save pass policy. Please try again.'
        )
      );
    } finally {
      setLoading('savePolicy', false);
    }
  };

  const content = (
    <div className={styles['settings-card']}>
      <h3>Pass Policy</h3>
      <p className={styles.paragraph}>
        Configure school-wide pass limits: how many passes each student may use
        per interval, and how many passes can be active at once.
      </p>

      {loadingPolicy ? (
        <p>Loading policy...</p>
      ) : (
        <div className="pass-limits-form">
          {!policyExists && (
            <p className={`no-results ${styles.paragraph}`}>
              No pass policy configured yet for this school. Set values below
              and save to create one.
            </p>
          )}

          <div className={styles['form-group']}>
            <label htmlFor="policy-max-active">
              Max Active Passes (school-wide)
            </label>
            <input
              id="policy-max-active"
              type="number"
              min="0"
              placeholder="Unlimited"
              value={maxActivePasses}
              onChange={(e) => setMaxActivePasses(e.target.value)}
              className={styles['form-input']}
              disabled={loadingStates.savePolicy}
            />
            <small>
              Maximum number of passes that may be active at the same time
              across the whole school. Leave blank for unlimited.
            </small>
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="policy-interval">Interval</label>
            <select
              id="policy-interval"
              value={policyInterval}
              onChange={(e) => setPolicyInterval(e.target.value)}
              className={styles['form-input']}
              disabled={loadingStates.savePolicy}
            >
              {INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <small>
              The rolling window that "Max Passes per Interval" below is
              measured over. Also required before a per-student limit can be set
              in Student Pass Limits.
            </small>
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="policy-max-per-interval">
              Max Passes per Interval (per student)
            </label>
            <input
              id="policy-max-per-interval"
              type="number"
              min="1"
              placeholder="Unlimited"
              value={maxPerInterval}
              onChange={(e) => setMaxPerInterval(e.target.value)}
              className={styles['form-input']}
              disabled={loadingStates.savePolicy}
            />
            <small>
              Maximum passes a single student may request per interval. Must be
              set together with Interval, or both left blank. Individual
              students can be overridden in Student Pass Limits below.
            </small>
          </div>

          <div className={adminStyles['button-group']}>
            <button
              onClick={savePolicy}
              disabled={loadingStates.savePolicy}
              className={adminStyles['add-btn']}
            >
              {loadingStates.savePolicy ? 'Saving...' : 'Save Policy'}
            </button>
            <button
              onClick={fetchPolicy}
              disabled={loadingStates.savePolicy || loadingPolicy}
              className={adminStyles['remove-btn']}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <CollapsibleSection
      title="Pass Policy"
      icon="⚙️"
      id="pass-policy"
      defaultExpanded={false}
    >
      {content}
    </CollapsibleSection>
  );
};

export default PassPolicyPanel;
